import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { SelectButton } from "primereact/selectbutton";
import styled from "styled-components";

const FIELD_HEIGHT = "2.75rem";

/* The fields sit on a gray container, so a plain gray-200 hairline reads too
   faint; nudge it toward gray-400. */
const FIELD_BORDER =
  "color-mix(in srgb, var(--brand-gray-400) 35%, var(--brand-gray-200))";

export const StyledInputNumber = styled(InputNumber)`
  width: 100%;

  .p-inputnumber-input {
    width: 100%;
    height: ${FIELD_HEIGHT};
    padding: 0.75rem 1rem;
    border-radius: 12px;
    font-size: 0.875rem;
    color: var(--brand-black);
    background: var(--brand-gray-50);
    border: 1px solid ${FIELD_BORDER};
    box-shadow: none;
  }

  .p-inputnumber-input::placeholder {
    color: var(--brand-gray-400);
  }

  .p-inputnumber-input:enabled:hover {
    border-color: var(--brand-gray-400);
  }

  .p-inputnumber-input:enabled:focus {
    border-color: var(--brand-purple-500);
    box-shadow: 0 0 0 3px
      color-mix(in srgb, var(--brand-purple-500) 20%, transparent);
  }
`;

export const StyledDropdown = styled(Dropdown)`
  &.p-dropdown {
    width: 100%;
    height: ${FIELD_HEIGHT};
    align-items: center;
    border-radius: 12px;
    background: var(--brand-gray-50);
    border: 1px solid ${FIELD_BORDER};
    box-shadow: none;
  }

  &.p-dropdown:not(.p-disabled):hover {
    border-color: var(--brand-gray-400);
  }

  &.p-dropdown:not(.p-disabled).p-focus {
    border-color: var(--brand-purple-500);
    box-shadow: 0 0 0 3px
      color-mix(in srgb, var(--brand-purple-500) 20%, transparent);
  }

  /* The dropdown's own label carries .p-inputtext, so strip the box it would
     otherwise draw inside the dropdown's border. */
  .p-dropdown-label.p-inputtext {
    height: auto;
    padding: 0 0 0 1rem;
    background: transparent;
    border: none;
    box-shadow: none;
    font-size: 0.875rem;
    color: var(--brand-black);
  }

  .p-dropdown-trigger {
    color: var(--brand-purple-700);
  }
`;

/** The $ / % down-payment type, rendered as a segmented switch. */
export const StyledTypeSwitch = styled(SelectButton)`
  &.p-selectbutton {
    display: flex;
    height: ${FIELD_HEIGHT};
    gap: 0.25rem;
    padding: 0.25rem;
    border-radius: 12px;
    background: var(--brand-gray-50);
    border: 1px solid ${FIELD_BORDER};
  }

  &.p-selectbutton .p-button {
    flex: 1;
    margin: 0;
    padding: 0 0.75rem;
    border: none;
    border-radius: 8px !important;
    background: transparent;
    color: var(--brand-gray-600);
    font-size: 0.875rem;
    font-weight: var(--font-weight-semibold);
    box-shadow: none;
  }

  &.p-selectbutton .p-button:not(.p-disabled):not(.p-highlight):hover {
    background: var(--brand-gray-100);
    color: var(--brand-black);
  }

  &.p-selectbutton .p-button.p-highlight,
  &.p-selectbutton .p-button.p-highlight:hover {
    background: var(--brand-purple-600);
    border-color: var(--brand-purple-600);
    color: var(--brand-white);
  }
`;

/**
 * Joins the down-payment amount input and the $ / % switch into one control:
 * the input squares off its right edge and the switch its left.
 */
export const StyledMergedRow = styled.div`
  display: flex;
  align-items: flex-start;

  .amount-side {
    flex: 1;
    min-width: 0;
  }

  .amount-side.is-hidden {
    display: none;
  }

  .amount-side .p-inputnumber .p-inputnumber-input {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: none;
  }

  .type-side {
    flex-shrink: 0;
  }

  .type-side .p-component.p-selectbutton {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  /* Focusing either half lights up the whole control: both borders turn
     purple and a single ring is drawn around the pair rather than around
     the input alone. */
  border-radius: 12px;

  &:focus-within {
    box-shadow: 0 0 0 3px
      color-mix(in srgb, var(--brand-purple-500) 20%, transparent);
  }

  &:focus-within .amount-side .p-inputnumber .p-inputnumber-input {
    border-color: var(--brand-purple-500);
  }

  &:focus-within
    .amount-side
    .p-inputnumber
    .p-inputnumber-input:enabled:focus {
    box-shadow: none;
  }

  &:focus-within .type-side .p-component.p-selectbutton {
    border-color: var(--brand-purple-500);
  }
`;
